import { type ReactNode, useEffect, useRef } from "react";
import { BrandWordmark } from "../components/brand/BrandWordmark";
import { LifterWordmarkSpriteSource } from "../components/brand/lifter-wordmark-sprite";
import { PageHeader } from "../components/ui/page-header";
import { Panel, PanelBody } from "../components/ui/panel";
import { useBrandedDocumentTitle, useInstanceBranding } from "./instance-branding";

/**
 * Shared shell for the pre-auth pages: tenant wordmark above a centered card.
 * The tenant logo renders when the instance branding carries one; otherwise
 * the Lifter wordmark stands in (the same posture as the portal chrome before
 * a logo is configured).
 */
export function AuthPageFrame({
  children,
  focusTitle = false,
  title,
}: {
  children: ReactNode;
  focusTitle?: boolean;
  title: string;
}) {
  const branding = useInstanceBranding();
  const frameRef = useRef<HTMLElement>(null);
  const wordmark = branding?.logoUrl
    ? {
        slug: "tenant",
        displayName: branding.displayName,
        logoUrl: branding.logoUrl,
      }
    : { slug: "lifter", displayName: null, logoUrl: null };

  useBrandedDocumentTitle(title);

  useEffect(() => {
    if (focusTitle) frameRef.current?.querySelector("h1")?.focus();
  }, [focusTitle]);

  return (
    <main
      className="flex min-h-dvh flex-col items-center justify-center gap-8 bg-[var(--color-page-bg)] px-6 py-10"
      ref={frameRef}
    >
      <LifterWordmarkSpriteSource />
      <BrandWordmark branding={wordmark} size="account" />
      <Panel className="w-full max-w-sm">
        <PanelBody className="flex flex-col gap-5">
          <PageHeader title={title} />
          {children}
        </PanelBody>
      </Panel>
    </main>
  );
}

/** Inline server-side failure line for the auth forms. */
export function AuthFormError({ message }: { message: string | undefined }) {
  if (!message) return null;
  return (
    <p className="type-label text-[var(--color-danger)]" role="alert">
      {message}
    </p>
  );
}
